/* Monopoly-style Game Board Builder — Hooyah Woodworks
 * Vanilla JS. No dependencies.
 * 40-space board: 4 corners + 3 Chance + 3 Community Chest are fixed,
 * the remaining 30 spaces (22 streets + 4 railroads + 2 utilities + 2 taxes) are customer-named.
 * Variant resolution is generic: one picker per product option, minus the locked option.
 * Cart: POST /cart/add.js — one line, full design saved as _Design JSON.
 */
(function () {
  'use strict';

  const CFG = window.MBB_CONFIG;
  if (!CFG) return;
  const T = CFG.text || {};
  const NAME_MAX = Number(CFG.nameMax) || 24;
  const PRICE_MAX = Number(CFG.priceMax) || 8;
  // Treat a missing setting as "required" — only an explicit false turns the gate off.
  const REQUIRE_ALL = CFG.requireAll !== false;

  /* ── Board definition (standard US Monopoly order, GO = index 0) ─── */
  const BOARD = [
    { i: 0,  type: 'corner',   corner: 'go' },
    { i: 1,  type: 'street',   group: 'brown' },
    { i: 2,  type: 'chest' },
    { i: 3,  type: 'street',   group: 'brown' },
    { i: 4,  type: 'tax',      group: 'tax' },
    { i: 5,  type: 'railroad', group: 'railroad' },
    { i: 6,  type: 'street',   group: 'lightblue' },
    { i: 7,  type: 'chance' },
    { i: 8,  type: 'street',   group: 'lightblue' },
    { i: 9,  type: 'street',   group: 'lightblue' },
    { i: 10, type: 'corner',   corner: 'jail' },
    { i: 11, type: 'street',   group: 'pink' },
    { i: 12, type: 'utility',  group: 'utility' },
    { i: 13, type: 'street',   group: 'pink' },
    { i: 14, type: 'street',   group: 'pink' },
    { i: 15, type: 'railroad', group: 'railroad' },
    { i: 16, type: 'street',   group: 'orange' },
    { i: 17, type: 'chest' },
    { i: 18, type: 'street',   group: 'orange' },
    { i: 19, type: 'street',   group: 'orange' },
    { i: 20, type: 'corner',   corner: 'free' },
    { i: 21, type: 'street',   group: 'red' },
    { i: 22, type: 'chance' },
    { i: 23, type: 'street',   group: 'red' },
    { i: 24, type: 'street',   group: 'red' },
    { i: 25, type: 'railroad', group: 'railroad' },
    { i: 26, type: 'street',   group: 'yellow' },
    { i: 27, type: 'street',   group: 'yellow' },
    { i: 28, type: 'utility',  group: 'utility' },
    { i: 29, type: 'street',   group: 'yellow' },
    { i: 30, type: 'corner',   corner: 'gotojail' },
    { i: 31, type: 'street',   group: 'green' },
    { i: 32, type: 'street',   group: 'green' },
    { i: 33, type: 'chest' },
    { i: 34, type: 'street',   group: 'green' },
    { i: 35, type: 'railroad', group: 'railroad' },
    { i: 36, type: 'chance' },
    { i: 37, type: 'street',   group: 'darkblue' },
    { i: 38, type: 'tax',      group: 'tax' },
    { i: 39, type: 'street',   group: 'darkblue' }
  ];

  const GROUP_ORDER = ['brown', 'lightblue', 'pink', 'orange', 'red', 'yellow',
    'green', 'darkblue', 'railroad', 'utility', 'tax'];

  const EDITABLE = BOARD.filter(sp => sp.type === 'street' || sp.type === 'railroad' ||
    sp.type === 'utility' || sp.type === 'tax');

  // Field labels count 1..30 in play order. The raw board index (which skips corners and
  // card spaces) would read "Space 37 / Space 39" and look like fields had gone missing.
  const ORDINAL = {};
  EDITABLE.forEach((sp, n) => { ORDINAL[sp.i] = n + 1; });

  const CORNER_ORDER = ['go', 'jail', 'free', 'gotojail'];

  /* Grid placement: 11×11. Play runs counter-clockwise from GO (bottom-right). */
  function place(i) {
    if (i === 0)  return { row: 11, col: 11,     side: 'corner', pos: 'br' };
    if (i < 10)   return { row: 11, col: 11 - i, side: 'bottom' };
    if (i === 10) return { row: 11, col: 1,      side: 'corner', pos: 'bl' };
    if (i < 20)   return { row: 21 - i, col: 1,  side: 'left' };
    if (i === 20) return { row: 1,  col: 1,      side: 'corner', pos: 'tl' };
    if (i < 30)   return { row: 1,  col: i - 19, side: 'top' };
    if (i === 30) return { row: 1,  col: 11,     side: 'corner', pos: 'tr' };
    return { row: i - 29, col: 11, side: 'right' };
  }

  /* ── State ─── */
  const state = {
    builderId: 'MBB-' + Date.now().toString(36).toUpperCase() + '-' +
      Math.random().toString(36).slice(2, 6).toUpperCase(),
    names: {},     // index -> string
    prices: {},    // index -> string (optional second line)
    corners: {},   // corner key -> override string
    center: ['', '', ''],
    centerMode: 'text',      // 'text' | 'logo' | 'standard' — exactly one
    namingMode: 'standard',  // 'standard' | 'some' | 'custom'
    pieces: 'none',          // 'none' | 'cards' | 'pieces'
    logoName: '',
    logoUrl: '',   // object URL, preview only
    options: [],   // selected value per product option index
    step: 'board'
  };
  EDITABLE.forEach(sp => { state.names[sp.i] = ''; state.prices[sp.i] = ''; });

  const STORE_KEY = 'mbb:' + (CFG.productHandle || 'board') + ':v1';

  /* ── Elements ─── */
  const root = document.getElementById('mbb-root');
  if (!root) return;
  const els = {
    steps:      root.querySelectorAll('.mbb__step'),
    panels:     root.querySelectorAll('.mbb__panel-step'),
    optionWrap: root.querySelector('#mbb-options'),
    groupsWrap: root.querySelector('#mbb-groups'),
    namingModes: root.querySelector('#mbb-naming-modes'),
    namingBody: root.querySelector('#mbb-naming-body'),
    piecesModes: root.querySelector('#mbb-pieces-modes'),
    cornerCount: root.querySelector('#mbb-corner-count'),
    cornerWrap: root.querySelector('#mbb-corners'),
    board:      root.querySelector('#mbb-board'),
    review:     root.querySelector('#mbb-review'),
    atc:        root.querySelector('#mbb-atc'),
    atcBlocked: root.querySelector('#mbb-atc-blocked'),
    atcPrice:   root.querySelector('#mbb-atc-price'),
    readout:    root.querySelector('#mbb-readout'),
    readoutLab: root.querySelector('#mbb-readout-label'),
    readoutVal: root.querySelector('#mbb-readout-value'),
    total:      root.querySelector('#mbb-total'),
    count:      root.querySelector('#mbb-count'),
    progFill:   root.querySelector('#mbb-progress-fill'),
    progText:   root.querySelector('#mbb-progress-text'),
    saved:      root.querySelector('#mbb-saved'),
    error:      root.querySelector('#mbb-error'),
    center1:    root.querySelector('#mbb-center-1'),
    center2:    root.querySelector('#mbb-center-2'),
    center3:    root.querySelector('#mbb-center-3'),
    form:       root.querySelector('#mbb-form'),
    variantId:  root.querySelector('#mbb-variant-id'),
    propsWrap:  root.querySelector('#mbb-props'),
    centerModes: root.querySelector('#mbb-center-modes'),
    centerText: root.querySelector('#mbb-center-text'),
    centerLogo: root.querySelector('#mbb-center-logo'),
    logoFile:   root.querySelector('#mbb-logo-file'),
    logoChosen: root.querySelector('#mbb-logo-chosen'),
    logoThumb:  root.querySelector('#mbb-logo-thumb'),
    logoNameEl: root.querySelector('#mbb-logo-name'),
    logoSize:   root.querySelector('#mbb-logo-size'),
    logoRemove: root.querySelector('#mbb-logo-remove'),
    logoError:  root.querySelector('#mbb-logo-error'),
    logoReattach: root.querySelector('#mbb-logo-reattach'),
    lightbox:   root.querySelector('#mbb-lightbox'),
    lbImg:      root.querySelector('#mbb-lb-img'),
    lbClose:    root.querySelector('#mbb-lb-close'),
    lbPrev:     root.querySelector('#mbb-lb-prev'),
    lbNext:     root.querySelector('#mbb-lb-next')
  };

  // Blocks user-initiated form submits (Enter in any of the 37 text fields). The logo
  // path flips this and calls form.submit(), which does not fire the submit event.
  let allowSubmit = false;

  const spaceEls = {};   // index -> { el, name, price }
  const inputEls = {};   // index -> input
  const groupEls = {};   // group key -> { el, count }
  let centerEl = null, centerNodes = null;

  /* ── Money ─── */
  function money(amount) {
    const fmt = CFG.moneyFormat || '${{amount}}';
    const n = Number(amount) || 0;
    function withComma(v, dec) {
      const parts = v.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join(dec ? '.' : '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return withComma(n, 0);
        case 'amount_with_comma_separator': return withComma(n, 2).replace(/,/g, 'X').replace('.', ',').replace(/X/g, '.');
        case 'amount_no_decimals_with_comma_separator': return withComma(n, 0).replace(/,/g, '.');
        default: return withComma(n, 2);
      }
    });
  }

  /* ── Variant resolution (generic across product options) ─── */
  const OPT_NAMES = CFG.optionNames || [];
  const VARIANTS = CFG.variants || [];
  const norm = s => String(s == null ? '' : s).trim().toLowerCase();
  // À la carte model: the main cart line is always the BASE board — Layout and the
  // cards/pieces option pinned to their base values, wood chosen by the customer — and
  // every customization is a priced add-on line item from the Game Board Customization
  // product, tied to the board by _Builder ID. What the customer did decides which
  // add-on lines ride along; the add-on variant prices decide what they cost.
  const BASE_CFG = CFG.base || {};
  const ADDONS = CFG.addons || {};
  const SOME_LIMIT = Number(CFG.someLimit) || 4;

  function resolveManaged(optName, optValue) {
    const idx = optName ? OPT_NAMES.findIndex(n => norm(n) === norm(optName)) : -1;
    if (idx < 0) return { idx: -1, value: '' };
    const hit = VARIANTS.find(v => norm(v.opts[idx]) === norm(optValue));
    return { idx: hit ? idx : -1, value: hit ? hit.opts[idx] : '' };
  }
  const baseLayout = resolveManaged(BASE_CFG.layoutName, BASE_CFG.layoutValue);
  const basePieces = resolveManaged(BASE_CFG.piecesName, BASE_CFG.piecesValue);
  // Options the builder pins to base values; everything else (wood) renders as a picker.
  const MANAGED = [baseLayout.idx, basePieces.idx].filter(i => i >= 0);

  function applyBase() {
    if (baseLayout.idx >= 0) state.options[baseLayout.idx] = baseLayout.value;
    if (basePieces.idx >= 0) state.options[basePieces.idx] = basePieces.value;
    if (!activeVariant()) {
      OPT_NAMES.forEach((_, j) => {
        if (MANAGED.indexOf(j) >= 0 || activeVariant()) return;
        const fallback = valuesFor(j).find(x => x.enabled);
        if (fallback) state.options[j] = fallback.value;
      });
    }
  }

  function addon(key) {
    const a = ADDONS[key] || {};
    return { id: String(a.id == null ? '' : a.id).trim(), price: Number(a.price) || 0 };
  }

  // How many spaces read differently from the classic set — this, not the chosen mode,
  // is what the customer is billed for.
  function changedCount() {
    return EDITABLE.reduce((n, sp) => {
      const val = (state.names[sp.i] || '').trim();
      return n + (val && val !== stdName(sp.i) ? 1 : 0);
    }, 0);
  }
  // Corners charge per corner actually changed — retyping the classic text is free.
  function changedCorners() {
    return CORNER_ORDER.filter(k => {
      const val = (state.corners[k] || '').trim();
      return val && val !== ((CFG.corners && CFG.corners[k]) || '').trim();
    });
  }

  // The add-on lines this build has earned: [{key, id, price, qty, label}].
  function addOnLines() {
    const lines = [];
    function push(key, qty, label) {
      const a = addon(key);
      if (qty > 0) lines.push({ key: key, id: a.id, price: a.price, qty: qty, label: label });
    }
    if (state.centerMode === 'logo' && logoFile()) {
      push('centerLogo', 1, T.addonCenterLogo || 'Center logo or image');
    } else if (state.centerMode === 'text' && state.center.some(s => (s || '').trim())) {
      push('centerText', 1, T.addonCenterText || 'Custom center text');
    }
    const n = changedCount();
    if (state.namingMode !== 'standard' && n > 0) {
      if (state.namingMode === 'custom' || n > SOME_LIMIT) {
        push('customNames', 1, T.addonCustomNames || 'Fully custom property names');
      } else {
        push('rename4', 1, T.addonRename4 || 'Rename up to 4 property spaces');
      }
    }
    push('corner', changedCorners().length, T.addonCorner || 'Corner rename');
    if (state.pieces === 'cards' || state.pieces === 'pieces') {
      push('cards', 1, T.addonCards || 'Custom property cards & houses/hotels');
    }
    if (state.pieces === 'pieces') {
      push('pieces', 1, T.addonPieces || 'Metal game pieces upgrade');
    }
    return lines;
  }

  function addOnTotal() {
    return addOnLines().reduce((sum, l) => sum + l.price * l.qty, 0);
  }
  // A paid customization whose add-on variant ID is blank could not be charged —
  // block checkout rather than silently give it away (the exact bug this replaces).
  function unchargeableAddons() {
    return addOnLines().filter(l => !l.id);
  }

  function initOptions() {
    OPT_NAMES.forEach((name, idx) => {
      if (idx === baseLayout.idx) { state.options[idx] = baseLayout.value; return; }
      if (idx === basePieces.idx) { state.options[idx] = basePieces.value; return; }
      state.options[idx] = null;
    });
    // Default every unlocked option to the first value that yields a real variant.
    OPT_NAMES.forEach((name, idx) => {
      if (state.options[idx]) return;
      const vals = valuesFor(idx);
      const first = vals.find(v => v.enabled) || vals[0];
      state.options[idx] = first ? first.value : null;
    });
  }

  function valuesFor(idx) {
    const seen = [];
    VARIANTS.forEach(v => {
      const val = v.opts[idx];
      if (val == null || seen.some(s => s.value === val)) return;
      seen.push({ value: val, enabled: false });
    });
    seen.forEach(s => {
      s.enabled = VARIANTS.some(v => {
        if (v.opts[idx] !== s.value) return false;
        return state.options.every((sel, j) => j === idx || sel == null || v.opts[j] === sel);
      });
    });
    return seen;
  }

  function activeVariant() {
    return VARIANTS.find(v => state.options.every((sel, j) => sel == null || v.opts[j] === sel)) || null;
  }

  function selectOption(idx, value) {
    state.options[idx] = value;
    // Repair any other selection that no longer resolves to a variant.
    if (!activeVariant()) {
      OPT_NAMES.forEach((_, j) => {
        if (j === idx || MANAGED.indexOf(j) >= 0) return;
        if (activeVariant()) return;
        const fallback = valuesFor(j).find(v => v.enabled);
        if (fallback) state.options[j] = fallback.value;
      });
    }
    renderOptions();
    updateTotals();
    save();
  }

  function renderOptions() {
    if (!els.optionWrap) return;
    els.optionWrap.innerHTML = '';
    OPT_NAMES.forEach((name, idx) => {
      if (MANAGED.indexOf(idx) >= 0) return;
      const h = document.createElement('h4');
      h.className = 'mbb__sub-label';
      h.textContent = name;
      els.optionWrap.appendChild(h);

      const row = document.createElement('div');
      row.className = 'mbb__pill-row';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', name);
      valuesFor(idx).forEach(v => {
        const b = document.createElement('button');
        b.type = 'button';
        const on = state.options[idx] === v.value;
        b.className = 'mbb__pill mbb__pill--stack' + (on ? ' is-active' : '');
        // Selected state must be programmatic, not just a coloured ring.
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (!v.enabled) b.disabled = true;
        const label = document.createElement('span');
        label.textContent = v.value;
        b.appendChild(label);
        const probe = state.options.slice();
        probe[idx] = v.value;
        const match = VARIANTS.find(vr => probe.every((sel, j) => sel == null || vr.opts[j] === sel));
        if (match) {
          const small = document.createElement('small');
          small.textContent = money(match.price);
          b.appendChild(small);
        }
        b.addEventListener('click', () => selectOption(idx, v.value));
        row.appendChild(b);
      });
      els.optionWrap.appendChild(row);
    });
    // Mode pills quote the price of their path — refresh whenever selections change.
    updateModePrices();
  }

  /* ── Board rendering ─── */
  function groupLabel(key) { return (CFG.groups && CFG.groups[key]) || key; }
  function groupColor(key) { return (CFG.colors && CFG.colors[key]) || '#cccccc'; }
  function cornerLabel(key) { return state.corners[key] || (CFG.corners && CFG.corners[key]) || ''; }

  function buildBoard() {
    if (!els.board) return;
    els.board.innerHTML = '';

    BOARD.forEach(sp => {
      const p = place(sp.i);
      const cell = document.createElement('div');
      cell.className = 'mbb__space mbb__space--' + p.side;
      if (p.side === 'corner') cell.classList.add('mbb__space--corner', 'mbb__pos--' + p.pos);
      cell.style.gridRow = String(p.row);
      cell.style.gridColumn = String(p.col);
      cell.dataset.index = String(sp.i);

      const inner = document.createElement('div');
      inner.className = 'mbb__space-inner';

      const isStreet = sp.type === 'street';
      if (isStreet) {
        const band = document.createElement('div');
        band.className = 'mbb__band';
        band.style.background = groupColor(sp.group);
        inner.appendChild(band);
      } else {
        cell.classList.add('mbb__space--noband');
      }

      const body = document.createElement('div');
      body.className = 'mbb__space-body';

      if (sp.type === 'chance' || sp.type === 'chest') {
        const icon = document.createElement('div');
        icon.className = 'mbb__space-icon';
        icon.textContent = sp.type === 'chance' ? '?' : '✦';
        const label = document.createElement('div');
        label.className = 'mbb__space-name';
        label.textContent = sp.type === 'chance'
          ? ((CFG.cards && CFG.cards.chance) || 'CHANCE')
          : ((CFG.cards && CFG.cards.chest) || 'COMMUNITY CHEST');
        body.appendChild(icon);
        body.appendChild(label);
        cell.classList.add('mbb__space--fixed');
      } else if (sp.type === 'corner') {
        const label = document.createElement('div');
        label.className = 'mbb__space-name';
        body.appendChild(label);
        spaceEls[sp.i] = { el: cell, name: label, price: null };
      } else {
        const label = document.createElement('div');
        label.className = 'mbb__space-name';
        const price = document.createElement('div');
        price.className = 'mbb__space-price';
        body.appendChild(label);
        if (CFG.showPrices) body.appendChild(price);
        if (!isStreet) {
          const type = document.createElement('div');
          type.className = 'mbb__space-type';
          type.textContent = (CFG.typeLabels && CFG.typeLabels[sp.type]) || '';
          body.insertBefore(type, label);
        }
        spaceEls[sp.i] = { el: cell, name: label, price: price };
      }

      inner.appendChild(body);
      cell.appendChild(inner);
      // Only advertise a click affordance on cells that actually lead somewhere —
      // Chance and Community Chest have no field to jump to.
      if (sp.corner) {
        cell.classList.add('mbb__space--link');
        cell.addEventListener('click', () => focusCorner(sp.corner));
      } else if (sp.type !== 'chance' && sp.type !== 'chest') {
        cell.classList.add('mbb__space--link');
        cell.addEventListener('click', () => focusSpace(sp.i));
      }
      els.board.appendChild(cell);
    });

    // Center
    const c = document.createElement('div');
    c.className = 'mbb__center';
    const ci = document.createElement('div');
    ci.className = 'mbb__center-inner';
    const limg = document.createElement('img');
    limg.className = 'mbb__center-logo'; limg.alt = ''; limg.hidden = true;
    const l1 = document.createElement('div'); l1.className = 'mbb__center-1';
    const l2 = document.createElement('div'); l2.className = 'mbb__center-2';
    const l3 = document.createElement('div'); l3.className = 'mbb__center-3';
    ci.appendChild(limg); ci.appendChild(l1); ci.appendChild(l2); ci.appendChild(l3);
    c.appendChild(ci);
    c.addEventListener('click', () => {
      goStep('center');
      const target = state.centerMode === 'logo' ? els.logoFile : els.center1;
      if (target) target.focus();
    });
    els.board.appendChild(c);
    centerEl = c;
    centerNodes = { l1: l1, l2: l2, l3: l3, img: limg };

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(entries => {
        for (const e of entries) {
          els.board.style.setProperty('--mbb-px', Math.round(e.contentRect.width) + 'px');
        }
      }).observe(els.board);
    } else {
      els.board.style.setProperty('--mbb-px', els.board.offsetWidth + 'px');
      window.addEventListener('resize', () => {
        els.board.style.setProperty('--mbb-px', els.board.offsetWidth + 'px');
      });
    }
  }

  function paintBoard() {
    EDITABLE.forEach(sp => {
      const ref = spaceEls[sp.i];
      if (!ref) return;
      const val = spaceName(sp.i);
      ref.name.textContent = val || (T.emptySpace || '—');
      ref.name.classList.toggle('is-empty', !val);
      // Customer-entered prices only apply to customer-named boards.
      if (ref.price) ref.price.textContent = state.namingMode === 'standard' ? '' : (state.prices[sp.i] || '').trim();
    });
    CORNER_ORDER.forEach(key => {
      const sp = BOARD.find(b => b.corner === key);
      const ref = sp && spaceEls[sp.i];
      if (ref) ref.name.textContent = cornerLabel(key);
    });
    if (centerNodes) {
      // The optional 45° tilt is a text flourish — never rotate someone's logo.
      centerEl.classList.toggle('is-logo', state.centerMode === 'logo');
      // The classic center is a real preview — the red MONOPOLY-style banner —
      // not a muted placeholder.
      centerEl.classList.toggle('is-classic', state.centerMode === 'standard');
      if (state.centerMode === 'standard') {
        centerNodes.img.hidden = true;
        centerNodes.l1.hidden = false;
        centerNodes.l1.textContent = T.centerStandard || 'MONOPOLY';
        centerNodes.l2.textContent = '';
        centerNodes.l3.textContent = '';
        centerEl.classList.remove('is-empty');
      } else if (state.centerMode === 'logo') {
        const url = state.logoUrl;
        centerNodes.img.hidden = !url;
        if (url) centerNodes.img.src = url;
        centerNodes.l1.hidden = !!url;
        centerNodes.l1.textContent = url ? '' : (state.logoName || (T.centerEmptyLogo || 'Your logo goes here'));
        centerNodes.l2.textContent = '';
        centerNodes.l3.textContent = '';
        // Keyed on the preview, not the filename: a PDF/AI/EPS we cannot render shows its
        // filename in the muted placeholder style, so it reads as a note about the
        // attachment rather than as text we are about to engrave.
        centerEl.classList.toggle('is-empty', !state.logoUrl);
      } else {
        centerNodes.img.hidden = true;
        const has = (state.center[0] || '').trim();
        centerNodes.l1.hidden = false;
        centerNodes.l1.textContent = has || (T.centerEmpty || 'Your name goes here');
        centerNodes.l2.textContent = (state.center[1] || '').trim();
        centerNodes.l3.textContent = (state.center[2] || '').trim();
        centerEl.classList.toggle('is-empty', !has);
      }
    }
  }

  function focusSpace(i) {
    const input = inputEls[i];
    if (!input) return;
    const sp = BOARD.find(b => b.i === i);
    goStep('spaces');
    // In standard mode there is nothing to type — land on the mode choice instead.
    if (state.namingMode === 'standard') return;
    if (sp && sp.group) openGroup(sp.group);
    input.focus();
    input.select();
  }

  /* ── Property naming: standard / some / custom ─── */
  const STD = CFG.standardNames || {};
  function stdName(i) { return (STD[i] || STD[String(i)] || '').trim(); }
  // Indices whose current value was written by the to-'some' prefill, not typed by the
  // customer. Only these are safe to wipe when switching to fully-custom naming — a
  // customer who deliberately typed "Park Place" keeps it.
  const autoFilled = new Set();
  // What a space actually reads right now — the classic name in standard mode,
  // the customer's entry otherwise.
  function spaceName(i) {
    return state.namingMode === 'standard' ? stdName(i) : (state.names[i] || '').trim();
  }

  function setNamingMode(mode) {
    const m = (mode === 'some' || mode === 'custom') ? mode : 'standard';
    const prev = state.namingMode;
    state.namingMode = m;
    // Only transform entries on a real user switch — on restore the saved names are
    // already exactly what the customer left behind.
    if (!restoring && m !== prev) {
      if (m === 'some') {
        // Fill only the blanks; anything already typed survives.
        EDITABLE.forEach(sp => {
          if (!(state.names[sp.i] || '').trim()) {
            state.names[sp.i] = stdName(sp.i);
            if (state.names[sp.i]) autoFilled.add(sp.i);
            syncNameInput(sp.i);
          }
        });
      } else if (m === 'custom') {
        // Untouched prefills vanish; anything the customer typed stays — even a value
        // that happens to equal the classic name.
        EDITABLE.forEach(sp => {
          if (autoFilled.has(sp.i)) {
            autoFilled.delete(sp.i);
            state.names[sp.i] = '';
            syncNameInput(sp.i);
          }
        });
      }
    }
    if (els.namingBody) els.namingBody.hidden = m === 'standard';
    if (els.namingModes) {
      els.namingModes.querySelectorAll('[data-naming-mode]').forEach(b => {
        const on = b.dataset.namingMode === m;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    updateModePrices();
    paintBoard();
    updateTotals();
    save();
  }

  function syncNameInput(i) {
    const input = inputEls[i];
    if (!input) return;
    input.value = state.names[i] || '';
    input.classList.toggle('is-empty', !input.value.trim());
  }

  // Every choice pill wears its price tag — a fixed add-on charge, not a variant probe.
  function updateModePrices() {
    function set(b, price) {
      const el = b.querySelector('[data-mode-price]');
      if (el) el.textContent = price > 0 ? '+ ' + money(price) : (T.included || 'Included');
    }
    if (els.centerModes) {
      els.centerModes.querySelectorAll('[data-center-mode]').forEach(b => {
        const m = b.dataset.centerMode;
        set(b, m === 'text' ? addon('centerText').price : m === 'logo' ? addon('centerLogo').price : 0);
      });
    }
    if (els.namingModes) {
      els.namingModes.querySelectorAll('[data-naming-mode]').forEach(b => {
        const m = b.dataset.namingMode;
        set(b, m === 'some' ? addon('rename4').price : m === 'custom' ? addon('customNames').price : 0);
      });
    }
    if (els.piecesModes) {
      els.piecesModes.querySelectorAll('[data-pieces]').forEach(b => {
        const m = b.dataset.pieces;
        set(b, m === 'cards' ? addon('cards').price
          : m === 'pieces' ? addon('cards').price + addon('pieces').price : 0);
      });
    }
  }

  function setPiecesMode(mode) {
    state.pieces = (mode === 'cards' || mode === 'pieces') ? mode : 'none';
    if (els.piecesModes) {
      els.piecesModes.querySelectorAll('[data-pieces]').forEach(b => {
        const on = b.dataset.pieces === state.pieces;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    updateTotals();
    save();
  }

  /* ── Center: text, logo, or classic ─── */
  function logoFile() {
    return els.logoFile && els.logoFile.files && els.logoFile.files[0] ? els.logoFile.files[0] : null;
  }

  function humanSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
    return (bytes / 1048576).toFixed(bytes < 10485760 ? 1 : 0) + ' MB';
  }

  // The accept attribute is only a file-picker hint — it is trivially bypassed by
  // drag-and-drop or by picking "All files", so the same list is enforced here.
  function typeAllowed(f) {
    const accept = String(CFG.logoAccept || '').split(',')
      .map(a => a.trim().toLowerCase()).filter(Boolean);
    if (!accept.length) return true;
    const name = (f.name || '').toLowerCase();
    const mime = (f.type || '').toLowerCase();
    return accept.some(a => {
      if (a.charAt(0) === '.') return name.slice(-a.length) === a;
      if (a.slice(-2) === '/*') return mime.indexOf(a.slice(0, -1)) === 0;
      return mime && mime === a;
    });
  }

  function setCenterMode(mode) {
    state.centerMode = (mode === 'logo' || mode === 'standard') ? mode : 'text';
    const isLogo = state.centerMode === 'logo';
    if (els.centerText) els.centerText.hidden = state.centerMode !== 'text';
    if (els.centerLogo) els.centerLogo.hidden = !isLogo;
    // A disabled input is never submitted. That keeps an empty file part out of the
    // multipart body in text mode — and it is why the three text inputs carry no `name`
    // at all, so a logo order can never also drag stale typed lines along with it.
    if (els.logoFile) els.logoFile.disabled = !isLogo;
    if (els.centerModes) {
      els.centerModes.querySelectorAll('[data-center-mode]').forEach(b => {
        const on = b.dataset.centerMode === state.centerMode;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    paintBoard(); updateTotals(); save();
  }

  function releaseLogoUrl() {
    if (state.logoUrl) { try { URL.revokeObjectURL(state.logoUrl); } catch (e) { /* ignore */ } }
    state.logoUrl = '';
  }

  function clearLogo() {
    releaseLogoUrl();
    state.logoName = '';
    if (els.logoNameEl) els.logoNameEl.textContent = '';
    if (els.logoSize) els.logoSize.textContent = '';
    if (els.logoFile) els.logoFile.value = '';
    if (els.logoChosen) els.logoChosen.hidden = true;
    if (els.logoThumb) { els.logoThumb.removeAttribute('src'); els.logoThumb.hidden = true; }
    paintBoard(); updateTotals(); save();
  }

  function onLogoPick() {
    releaseLogoUrl();
    if (els.logoError) els.logoError.textContent = '';
    if (els.logoReattach) els.logoReattach.hidden = true;

    const f = logoFile();
    if (!f) { clearLogo(); return; }

    // Shopify's own ceiling is 20 MB; a larger setting cannot raise it.
    const maxMb = Math.min(Number(CFG.logoMaxMb) || 20, 20);
    if (f.size > maxMb * 1024 * 1024) {
      if (els.logoError) {
        els.logoError.textContent = (T.logoTooBig || 'That file is too large.') +
          ' ' + humanSize(f.size) + ' — ' + maxMb + ' MB max.';
      }
      clearLogo();
      return;
    }
    if (!typeAllowed(f)) {
      if (els.logoError) {
        els.logoError.textContent = (T.logoBadType ||
          'That file type will not engrave — send an image or vector file.') + ' (' + f.name + ')';
      }
      clearLogo();
      return;
    }

    state.logoName = f.name;
    if (els.logoNameEl) els.logoNameEl.textContent = f.name;
    if (els.logoSize) els.logoSize.textContent = humanSize(f.size);
    if (els.logoChosen) els.logoChosen.hidden = false;

    // Rasters and SVG preview in an <img>; PDF/AI/EPS cannot, so those fall back to
    // showing the filename on the board instead of a broken image.
    if (els.logoThumb) {
      if (/^image\//.test(f.type)) {
        state.logoUrl = URL.createObjectURL(f);
        els.logoThumb.src = state.logoUrl;
        els.logoThumb.hidden = false;
      } else {
        els.logoThumb.removeAttribute('src');
        els.logoThumb.hidden = true;
      }
    }
    paintBoard(); updateTotals(); save();
  }

  function focusCorner(key) {
    goStep('corners');
    const el = document.getElementById('mbb-corner-' + key);
    if (el) { el.focus(); el.select(); }
  }

  function highlight(i, on) {
    const ref = spaceEls[i];
    if (ref) ref.el.classList.toggle('is-focused', !!on);
    if (!els.readout) return;
    if (on) {
      const sp = BOARD.find(b => b.i === i);
      els.readout.classList.add('is-on');
      els.readoutLab.textContent = (T.spaceLabel || 'Space') + ' ' + ORDINAL[i] +
        (sp && sp.group ? ' · ' + groupLabel(sp.group) : '');
      els.readoutVal.textContent = (state.names[i] || '').trim();
    } else {
      els.readout.classList.remove('is-on');
    }
  }

  /* ── Panel: colour-group accordion ─── */
  function buildGroups() {
    if (!els.groupsWrap) return;
    els.groupsWrap.innerHTML = '';

    GROUP_ORDER.forEach((key, gi) => {
      const members = EDITABLE.filter(sp => sp.group === key);
      if (!members.length) return;

      const wrap = document.createElement('div');
      wrap.className = 'mbb__group' + (gi === 0 ? ' is-open' : '');
      wrap.dataset.group = key;

      const head = document.createElement('button');
      head.type = 'button';
      head.className = 'mbb__group-head';
      head.setAttribute('aria-expanded', gi === 0 ? 'true' : 'false');

      const sw = document.createElement('span');
      sw.className = 'mbb__group-swatch';
      sw.style.background = groupColor(key);

      const nm = document.createElement('span');
      nm.className = 'mbb__group-name';
      nm.textContent = groupLabel(key);

      const ct = document.createElement('span');
      ct.className = 'mbb__group-count';

      const cr = document.createElement('span');
      cr.className = 'mbb__group-caret';
      cr.textContent = '▶';

      head.appendChild(sw); head.appendChild(nm); head.appendChild(ct); head.appendChild(cr);
      head.addEventListener('click', () => {
        const open = wrap.classList.toggle('is-open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      const body = document.createElement('div');
      body.className = 'mbb__group-body';

      members.forEach(sp => {
        const field = document.createElement('div');
        field.className = 'mbb__field';

        const lab = document.createElement('label');
        lab.className = 'mbb__field-label';
        lab.setAttribute('for', 'mbb-space-' + sp.i);
        const labText = document.createElement('span');
        labText.textContent = (T.spaceLabel || 'Space') + ' ' + ORDINAL[sp.i];
        const pos = document.createElement('span');
        pos.className = 'mbb__field-pos';
        pos.textContent = sideName(sp.i);
        lab.appendChild(labText); lab.appendChild(pos);

        const row = document.createElement('div');
        row.className = 'mbb__field-row';

        const input = document.createElement('input');
        input.type = 'text';
        input.id = 'mbb-space-' + sp.i;
        input.className = 'mbb__input mbb__input--name';
        input.maxLength = NAME_MAX;
        input.placeholder = placeholderFor(sp);
        input.autocomplete = 'off';
        // Every space starts empty, so the "still needs filling" cue must be on from the
        // first paint — not only once a value has been typed and cleared.
        input.classList.add('is-empty');
        input.addEventListener('input', () => {
          autoFilled.delete(sp.i); // typed = customer-owned from here on
          state.names[sp.i] = input.value;
          input.classList.toggle('is-empty', !input.value.trim());
          if (els.readoutVal) els.readoutVal.textContent = input.value.trim();
          paintBoard(); updateTotals(); save();
        });
        input.addEventListener('focus', () => highlight(sp.i, true));
        input.addEventListener('blur', () => highlight(sp.i, false));
        row.appendChild(input);
        inputEls[sp.i] = input;

        if (CFG.showPrices) {
          const price = document.createElement('input');
          price.type = 'text';
          price.id = 'mbb-price-' + sp.i;
          price.className = 'mbb__input mbb__input--price';
          price.maxLength = PRICE_MAX;
          price.placeholder = T.pricePlaceholder || '$60';
          // Placeholder-only fields are unnamed to a screen reader, and the placeholder
          // disappears the moment anything is typed.
          price.setAttribute('aria-label',
            (T.priceLabel || 'Price shown on this space') + ' — ' +
            (T.spaceLabel || 'Space') + ' ' + ORDINAL[sp.i]);
          price.autocomplete = 'off';
          price.addEventListener('input', () => {
            state.prices[sp.i] = price.value;
            paintBoard(); save();
          });
          price.addEventListener('focus', () => highlight(sp.i, true));
          price.addEventListener('blur', () => highlight(sp.i, false));
          row.appendChild(price);
        }

        field.appendChild(lab);
        field.appendChild(row);
        body.appendChild(field);
      });

      wrap.appendChild(head);
      wrap.appendChild(body);
      els.groupsWrap.appendChild(wrap);
      groupEls[key] = { el: wrap, count: ct, total: members.length };
    });
  }

  function openGroup(key) {
    const g = groupEls[key];
    if (!g) return;
    g.el.classList.add('is-open');
    const head = g.el.querySelector('.mbb__group-head');
    if (head) head.setAttribute('aria-expanded', 'true');
  }

  function sideName(i) {
    const p = place(i);
    if (p.side === 'bottom') return T.sideBottom || 'bottom row';
    if (p.side === 'left')   return T.sideLeft   || 'left side';
    if (p.side === 'top')    return T.sideTop    || 'top row';
    if (p.side === 'right')  return T.sideRight  || 'right side';
    return '';
  }

  function placeholderFor(sp) {
    const ph = CFG.placeholders || {};
    if (sp.type === 'railroad') return ph.railroad || '';
    if (sp.type === 'utility')  return ph.utility  || '';
    if (sp.type === 'tax')      return ph.tax      || '';
    return ph.street || '';
  }

  /* ── Corners (optional overrides) ─── */
  function buildCorners() {
    if (!els.cornerWrap) return;
    els.cornerWrap.innerHTML = '';
    CORNER_ORDER.forEach(key => {
      const lab = document.createElement('label');
      lab.className = 'mbb__label';
      lab.setAttribute('for', 'mbb-corner-' + key);
      lab.textContent = (CFG.cornerLabels && CFG.cornerLabels[key]) || key;

      const input = document.createElement('input');
      input.type = 'text';
      input.id = 'mbb-corner-' + key;
      input.className = 'mbb__input';
      input.maxLength = NAME_MAX;
      input.placeholder = (CFG.corners && CFG.corners[key]) || '';
      input.autocomplete = 'off';
      input.addEventListener('input', () => {
        state.corners[key] = input.value;
        paintBoard(); updateTotals(); save();
      });

      els.cornerWrap.appendChild(lab);
      els.cornerWrap.appendChild(input);
    });
  }

  /* ── Progress / totals ─── */
  function filledCount() {
    return EDITABLE.reduce((n, sp) => n + (spaceName(sp.i) ? 1 : 0), 0);
  }
  function centerOk() {
    if (state.centerMode === 'standard') return true;
    return state.centerMode === 'logo' ? !!logoFile() : !!(state.center[0] || '').trim();
  }
  function missingGroups() {
    return GROUP_ORDER.filter(key =>
      EDITABLE.some(sp => sp.group === key && !spaceName(sp.i)));
  }
  function blockers() {
    const list = [];
    if (!centerOk()) {
      list.push(state.centerMode === 'logo'
        ? (T.logoMissing || 'Attach your logo file')
        : (T.reviewCenter || 'Center of the board'));
    }
    missingGroups().forEach(k => list.push(groupLabel(k)));
    return list;
  }
  // Separate from blockers(): these block regardless of require_all, because letting the
  // order through would give the customization away free.
  function configBlockers() {
    return unchargeableAddons().map(l =>
      (T.addonError || 'cannot be charged right now — please contact us') + ': ' + l.label);
  }

  function updateTotals() {
    const v = activeVariant();
    // Grand total = base board + every add-on line this build has earned.
    const price = (v ? v.price : 0) + addOnTotal();
    const n = filledCount();
    const total = EDITABLE.length;

    if (els.total)    els.total.textContent = money(price);
    if (els.atcPrice) els.atcPrice.textContent = money(price);
    if (els.count)    els.count.textContent = n + ' / ' + total + ' ' + (T.named || 'spaces named');

    if (state.namingMode === 'some') {
      // In 'some' mode every field is prefilled, so "30 of 30 named" says nothing —
      // what matters is how many renames they have used against the included allowance.
      const c = changedCount();
      const tmpl = c > SOME_LIMIT
        ? (T.someOver || '%n% spaces renamed — charged as fully custom names')
        : (T.someCount || '%n% of %limit% name changes used');
      if (els.progFill) els.progFill.style.width = Math.min(100, Math.round((c / SOME_LIMIT) * 100)) + '%';
      if (els.progText) els.progText.textContent = tmpl.replace('%n%', c).replace('%limit%', SOME_LIMIT);
    } else {
      if (els.progFill) els.progFill.style.width = Math.round((n / total) * 100) + '%';
      if (els.progText) els.progText.textContent = n + ' ' + (T.of || 'of') + ' ' + total + ' ' + (T.named || 'spaces named');
    }

    // Live corner tally: "2 corners renamed — + $10.00".
    if (els.cornerCount) {
      const c = changedCorners().length;
      const a = addon('corner');
      els.cornerCount.hidden = c === 0;
      els.cornerCount.textContent = c === 0 ? '' :
        (T.cornerCount || '%n% renamed — %price%')
          .replace('%n%', c)
          .replace('%price%', '+ ' + money(c * a.price));
    }

    Object.keys(groupEls).forEach(key => {
      const g = groupEls[key];
      const done = EDITABLE.filter(sp => sp.group === key && spaceName(sp.i)).length;
      g.count.textContent = done + ' / ' + g.total;
      g.el.classList.toggle('is-done', done === g.total);
    });

    // Only a missing variant hard-disables the button. Incompleteness is surfaced as text
    // instead, so the customer can see WHAT is missing rather than a mute grey button.
    if (els.atc) els.atc.disabled = !v;
    if (els.atcBlocked) {
      const miss = REQUIRE_ALL ? blockers() : [];
      els.atcBlocked.textContent = miss.length
        ? (T.incomplete || 'Still to fill in:') + ' ' + miss.join(', ')
        : '';
    }
    if (state.step === 'review') renderReview();
  }

  function renderReview() {
    if (!els.review) return;
    els.review.innerHTML = '';
    const v = activeVariant();

    function head(text) {
      const d = document.createElement('div');
      d.className = 'mbb__review-row mbb__review-head';
      d.textContent = text;
      els.review.appendChild(d);
    }
    function row(left, right, missing) {
      const d = document.createElement('div');
      d.className = 'mbb__review-row' + (missing ? ' mbb__review-row--missing' : '');
      const a = document.createElement('span'); a.textContent = left;
      const b = document.createElement('strong'); b.textContent = right == null ? '' : right;
      d.appendChild(a); d.appendChild(b);
      els.review.appendChild(d);
    }

    if (v) {
      head(T.reviewBoard || 'Board');
      // Only the options the customer actually picks (wood) belong here — the managed
      // base values would read like choices they never made.
      OPT_NAMES.forEach((name, idx) => {
        if (MANAGED.indexOf(idx) < 0) row(name, v.opts[idx]);
      });
      row(T.reviewBase || 'Base board', money(v.price));
    }

    // Itemized customization charges, exactly as they will appear in the cart.
    const lines = addOnLines();
    if (lines.length) {
      head(T.reviewAddons || 'Customization');
      lines.forEach(l => {
        const label = l.qty > 1 ? l.qty + ' × ' + l.label : l.label;
        row(label, money(l.price * l.qty));
      });
    }
    if (v) row(T.reviewPrice || 'Total', money(v.price + addOnTotal()));

    head(T.reviewCenter || 'Center of the board');
    if (state.centerMode === 'standard') {
      row(T.centerStandardReview || 'Classic center — no customization', '');
    } else if (state.centerMode === 'logo') {
      const f = logoFile();
      if (f) row((T.logoReview || 'Center logo') + ' — ' + f.name, humanSize(f.size));
      else row(T.logoMissing || 'Attach your logo file', '', true);
    } else {
      const cLines = state.center.map(s => (s || '').trim()).filter(Boolean);
      if (cLines.length) cLines.forEach(l => row(l, ''));
      else row(T.reviewCenterMissing || 'Not filled in yet', '', true);
    }

    head(T.propNaming || 'Property names');
    row(namingModeLabel(), '');
    if (state.namingMode !== 'standard') {
      GROUP_ORDER.forEach(key => {
        const members = EDITABLE.filter(sp => sp.group === key);
        if (!members.length) return;
        head(groupLabel(key));
        members.forEach(sp => {
          const val = (state.names[sp.i] || '').trim();
          const price = (state.prices[sp.i] || '').trim();
          row(val || (T.reviewMissing || 'Not filled in yet'), CFG.showPrices ? price : '', !val);
        });
      });
    }

    const overridden = CORNER_ORDER.filter(k => (state.corners[k] || '').trim());
    if (overridden.length) {
      head(T.reviewCorners || 'Corners');
      overridden.forEach(k => row((CFG.cornerLabels && CFG.cornerLabels[k]) || k, state.corners[k].trim()));
    }
  }

  /* ── Steps ─── */
  function goStep(step) {
    state.step = step;
    els.steps.forEach(b => {
      const on = b.dataset.step === step;
      b.classList.toggle('is-active', on);
      if (on) {
        b.setAttribute('aria-current', 'step');
        // The step strip scrolls horizontally with no visible scrollbar — later steps are
        // off-screen on a phone unless we bring the active one into view.
        if (b.scrollIntoView) b.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        b.removeAttribute('aria-current');
      }
    });
    els.panels.forEach(p => {
      const on = p.dataset.panel === step;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
    });
    if (step === 'review') renderReview();
  }

  /* ── Persistence ─── */
  let saveTimer = null;
  let restoring = false;
  function save() {
    // restore() calls setCenterMode(), which calls save() — writing half-restored state
    // back over the very record still being read. Ignore saves while restoring.
    if (restoring) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify({
          names: state.names, prices: state.prices, corners: state.corners,
          center: state.center, options: state.options,
          centerMode: state.centerMode, logoName: state.logoName,
          namingMode: state.namingMode,
          pieces: state.pieces,
          autoFilled: Array.from(autoFilled)
        }));
        if (els.saved) els.saved.textContent = T.saved || 'Saved on this device';
      } catch (e) { /* private mode / quota — not fatal */ }
    }, 350);
  }

  function restore() {
    let data = null;
    try { data = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return; }
    if (!data) return;
    restoring = true;
    try { restoreFrom(data); } finally { restoring = false; }
  }

  function restoreFrom(data) {
    if (data.names)   Object.keys(state.names).forEach(k => { if (data.names[k]) state.names[k] = data.names[k]; });
    if (Array.isArray(data.autoFilled)) data.autoFilled.forEach(i => autoFilled.add(Number(i)));
    if (data.prices)  Object.keys(state.prices).forEach(k => { if (data.prices[k]) state.prices[k] = data.prices[k]; });
    if (data.corners) state.corners = data.corners;
    if (Array.isArray(data.center)) state.center = [data.center[0] || '', data.center[1] || '', data.center[2] || ''];
    if (data.pieces === 'cards' || data.pieces === 'pieces') state.pieces = data.pieces;
    if (data.namingMode === 'some' || data.namingMode === 'custom') {
      state.namingMode = data.namingMode;
    } else if (data.namingMode === undefined &&
               EDITABLE.some(sp => (state.names[sp.i] || '').trim())) {
      // Legacy payload from before naming modes existed: that builder only made
      // fully-custom boards, so any typed name means a custom design in progress.
      // Testing === undefined matters — a current payload saved in 'standard' mode can
      // still carry leftover typed names and must stay 'standard'.
      state.namingMode = 'custom';
    }
    if (data.centerMode === 'logo' || data.centerMode === 'standard') state.centerMode = data.centerMode;
    if (Array.isArray(data.options) && data.options.length === OPT_NAMES.length) {
      const probe = data.options.slice();
      // The managed options are always pinned to base — only the wood survives restore.
      if (baseLayout.idx >= 0) probe[baseLayout.idx] = baseLayout.value;
      if (basePieces.idx >= 0) probe[basePieces.idx] = basePieces.value;
      if (VARIANTS.some(v => probe.every((sel, j) => sel == null || v.opts[j] === sel))) state.options = probe;
    }
    // Push restored values into the DOM
    EDITABLE.forEach(sp => {
      const input = inputEls[sp.i];
      if (input) { input.value = state.names[sp.i]; input.classList.toggle('is-empty', !input.value.trim()); }
    });
    CORNER_ORDER.forEach(k => {
      const el = document.getElementById('mbb-corner-' + k);
      if (el && state.corners[k]) el.value = state.corners[k];
    });
    if (els.center1) els.center1.value = state.center[0];
    if (els.center2) els.center2.value = state.center[1];
    if (els.center3) els.center3.value = state.center[2];
    // A File object cannot survive a page load. The *choice* comes back, the artwork
    // does not — state.logoName stays empty so centerOk() keeps blocking add-to-cart
    // until they re-attach it. (init syncs the mode UIs right after restore.)
    if (state.centerMode === 'logo' && data.logoName && els.logoReattach) {
      els.logoReattach.hidden = false;
    }
    if (CFG.showPrices) {
      EDITABLE.forEach(sp => {
        const field = inputEls[sp.i] && inputEls[sp.i].parentElement;
        const p = field && field.querySelector('.mbb__input--price');
        if (p) p.value = state.prices[sp.i];
      });
    }
  }

  function namingModeLabel() {
    if (state.namingMode === 'some') return T.namingReviewSome || 'Classic names, customized';
    if (state.namingMode === 'custom') return T.namingReviewCustom || 'Fully custom';
    return T.namingReviewStandard || 'Standard classic names';
  }

  /* ── Design payload ─── */
  function designJSON() {
    const v = activeVariant();
    return {
      v: 3,
      kind: 'monopoly-board',
      namingMode: state.namingMode,
      builderId: state.builderId,
      product: CFG.productHandle || '',
      variantId: v ? String(v.id) : '',
      options: OPT_NAMES.map((name, idx) => ({ name: name, value: v ? v.opts[idx] : state.options[idx] })),
      center: {
        mode: state.centerMode,
        lines: state.centerMode === 'text' ? state.center.map(s => (s || '').trim()) : [],
        logo: state.centerMode === 'logo' ? state.logoName : ''
      },
      corners: CORNER_ORDER.reduce((o, k) => { o[k] = cornerLabel(k); return o; }, {}),
      cards: {
        chance: (CFG.cards && CFG.cards.chance) || 'CHANCE',
        chest: (CFG.cards && CFG.cards.chest) || 'COMMUNITY CHEST'
      },
      spaces: BOARD.map(sp => {
        const o = { i: sp.i, type: sp.type };
        if (sp.group) o.group = sp.group;
        if (sp.corner) o.corner = sp.corner;
        if (state.names[sp.i] !== undefined) o.name = spaceName(sp.i);
        if (sp.corner) o.name = cornerLabel(sp.corner);
        // Every one of the 40 entries carries a name so the shop-floor sheet never has to
        // cross-reference the cards block.
        if (sp.type === 'chance') o.name = (CFG.cards && CFG.cards.chance) || 'CHANCE';
        if (sp.type === 'chest')  o.name = (CFG.cards && CFG.cards.chest)  || 'COMMUNITY CHEST';
        if (CFG.showPrices && state.namingMode !== 'standard' && state.prices[sp.i]) o.price = (state.prices[sp.i] || '').trim();
        return o;
      }),
      pieces: state.pieces,
      pricing: {
        base: v ? v.price : 0,
        addons: addOnLines().map(l => ({ key: l.key, label: l.label, qty: l.qty, each: l.price })),
        total: (v ? v.price : 0) + addOnTotal()
      }
    };
  }

  function groupSummary(key) {
    return EDITABLE.filter(sp => sp.group === key)
      .map(sp => (state.names[sp.i] || '').trim() || '—')
      .join(' · ');
  }

  /* ── Cart ─── */
  async function addToCart() {
    const v = activeVariant();
    if (!v) return;
    if (els.error) els.error.textContent = '';

    // Merchant-config failures block unconditionally — completing this order would give
    // a paid customization away free.
    const broken = configBlockers();
    if (broken.length) {
      if (els.error) els.error.textContent = broken.join(' · ');
      return;
    }

    const miss = REQUIRE_ALL ? blockers() : [];
    if (miss.length) {
      if (els.error) els.error.textContent = (T.incomplete || 'Still to fill in:') + ' ' + miss.join(', ');
      // Don't just name the gap — take them to it.
      if (!centerOk()) {
        goStep('center');
        const target = state.centerMode === 'logo' ? els.logoFile : els.center1;
        if (target) target.focus();
      } else {
        const g = missingGroups()[0];
        const sp = g && EDITABLE.find(x => x.group === g && !(state.names[x.i] || '').trim());
        if (sp) focusSpace(sp.i);
      }
      return;
    }

    els.atc.disabled = true;
    const originalHtml = els.atc.innerHTML;
    els.atc.textContent = T.atcAdding || 'Adding…';

    function restoreButton() {
      els.atc.disabled = false;
      els.atc.innerHTML = originalHtml;
      // innerHTML replaced the price node — re-point the reference at the new one.
      els.atcPrice = els.atc.querySelector('#mbb-atc-price');
    }

    const props = {
      '_Builder ID': state.builderId,
      '_Design JSON': JSON.stringify(designJSON())
    };
    props[T.propCenter || 'Center of board'] =
      state.centerMode === 'standard' ? (T.centerStandardReview || 'Classic center — no customization')
      : state.centerMode === 'logo'
        ? (state.logoName ? (T.logoReview || 'Center logo') + ': ' + state.logoName : '—')
        : (state.center.map(s => (s || '').trim()).filter(Boolean).join(' / ') || '—');
    props[T.propNaming || 'Property names'] = namingModeLabel();
    // A standard board's 30 classic names would just be noise on the order line —
    // the full list still travels in _Design JSON either way.
    if (state.namingMode !== 'standard') {
      GROUP_ORDER.forEach(key => { props[groupLabel(key)] = groupSummary(key); });
    }
    const overridden = CORNER_ORDER.filter(k => (state.corners[k] || '').trim());
    if (overridden.length) {
      props[T.propCorners || 'Corners'] = overridden
        .map(k => ((CFG.cornerLabels && CFG.cornerLabels[k]) || k) + ': ' + state.corners[k].trim())
        .join(' · ');
    }

    // A file can only reach Shopify through a real multipart POST — /cart/add.js with a
    // JSON body silently drops it. So the logo path hands off to the native form, which
    // uploads the artwork and returns a CDN link on the order line. Text-only orders keep
    // the AJAX add so errors can be shown inline without a page load.
    // Only take the native path when there is genuinely a file to carry. With
    // require_all switched off, logo mode with nothing attached would otherwise POST an
    // empty file part and create a line item with a broken artwork link.
    // Customization charges ride as their own cart lines, tied to the board by
    // _Builder ID — same pattern as the shadow box builder's awards and flag.
    const addonItems = addOnLines().map(l => ({
      id: Number(l.id),
      quantity: l.qty,
      properties: { '_Builder ID': state.builderId }
    }));

    if (state.centerMode === 'logo' && logoFile()) {
      // The add-on lines go in over AJAX FIRST — if that fails, nothing has been
      // charged and we can stop with an inline error. Only then does the native
      // multipart form carry the board + logo file (a JSON POST cannot carry a file).
      if (addonItems.length) {
        try {
          const res0 = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: addonItems })
          });
          const data0 = await res0.json();
          if (!res0.ok) throw new Error(data0.description || data0.message || 'Error');
        } catch (err) {
          restoreButton();
          updateTotals();
          if (els.error) els.error.textContent = (T.cartError || 'Could not add to cart:') + ' ' + err.message;
          return;
        }
      }
      els.variantId.value = String(v.id);
      els.propsWrap.innerHTML = '';
      Object.keys(props).forEach(k => {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[' + k + ']';
        hidden.value = props[k];
        els.propsWrap.appendChild(hidden);
      });
      els.atc.textContent = T.logoUploading || 'Uploading your logo…';
      // Deliberately NOT clearing the saved design here. A native POST can still fail
      // (oversize file, variant sold out, dropped connection) and the customer lands back
      // on a fresh page load — wiping first would take 30 names down with it. A stale
      // saved design beats a lost one; the AJAX branch still clears on confirmed success.
      allowSubmit = true;
      els.form.submit();
      return;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(v.id), quantity: 1, properties: props }].concat(addonItems) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Error');
      try { localStorage.removeItem(STORE_KEY); } catch (e) { /* ignore */ }
      window.location.href = CFG.cartUrl || '/cart';
    } catch (err) {
      restoreButton();
      updateTotals();
      if (els.error) els.error.textContent = (T.cartError || 'Could not add to cart:') + ' ' + err.message;
    }
  }

  /* ── Finished-board gallery ─── */
  function initGallery() {
    const shots = Array.prototype.slice.call(root.querySelectorAll('.mbb__shot'));
    if (!shots.length || !els.lightbox || !els.lbImg) return;

    // The step-1 filmstrip and the bottom grid are the same photos in the same order,
    // so de-dupe by URL — otherwise prev/next walks every picture twice.
    const urls = [];
    shots.forEach(b => {
      const u = b.dataset.full;
      if (u && urls.indexOf(u) < 0) urls.push(u);
    });
    if (!urls.length) return;

    let idx = 0, lastFocus = null;

    function show(i) {
      idx = (i + urls.length) % urls.length;
      els.lbImg.src = urls[idx];
      const src = shots.filter(b => b.dataset.full === urls[idx])[0];
      const thumb = src && src.querySelector('img');
      els.lbImg.alt = thumb ? thumb.alt : '';
    }
    function open(url) {
      lastFocus = document.activeElement;
      els.lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      show(Math.max(0, urls.indexOf(url)));
      if (els.lbClose) els.lbClose.focus();
    }
    function close() {
      els.lightbox.hidden = true;
      document.body.style.overflow = '';
      els.lbImg.removeAttribute('src');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    shots.forEach(b => b.addEventListener('click', () => open(b.dataset.full)));
    if (els.lbClose) els.lbClose.addEventListener('click', close);
    if (els.lbPrev) els.lbPrev.addEventListener('click', () => show(idx - 1));
    if (els.lbNext) els.lbNext.addEventListener('click', () => show(idx + 1));
    els.lightbox.addEventListener('click', e => { if (e.target === els.lightbox) close(); });
    document.addEventListener('keydown', e => {
      if (els.lightbox.hidden) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowLeft') { show(idx - 1); return; }
      if (e.key === 'ArrowRight') { show(idx + 1); return; }
      // aria-modal="true" promises focus stays inside. Without this, Tab walks straight
      // out into the page behind and the screen-reader user is stranded in hidden content.
      if (e.key === 'Tab') {
        const focusables = [els.lbClose, els.lbPrev, els.lbNext].filter(Boolean);
        if (!focusables.length) return;
        const first = focusables[0], last = focusables[focusables.length - 1];
        const at = focusables.indexOf(document.activeElement);
        if (e.shiftKey && (document.activeElement === first || at < 0)) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || at < 0)) {
          e.preventDefault(); first.focus();
        }
      }
    });
  }

  /* ── Init ─── */
  function init() {
    initOptions();
    buildBoard();
    buildGroups();
    buildCorners();
    initGallery();

    els.steps.forEach(b => b.addEventListener('click', () => goStep(b.dataset.step)));

    if (els.centerModes) {
      els.centerModes.querySelectorAll('[data-center-mode]').forEach(b => {
        b.addEventListener('click', () => setCenterMode(b.dataset.centerMode));
      });
    }
    if (els.namingModes) {
      els.namingModes.querySelectorAll('[data-naming-mode]').forEach(b => {
        b.addEventListener('click', () => setNamingMode(b.dataset.namingMode));
      });
    }
    if (els.piecesModes) {
      els.piecesModes.querySelectorAll('[data-pieces]').forEach(b => {
        b.addEventListener('click', () => setPiecesMode(b.dataset.pieces));
      });
    }
    if (els.logoFile) els.logoFile.addEventListener('change', onLogoPick);
    if (els.logoRemove) els.logoRemove.addEventListener('click', () => {
      clearLogo();
      if (els.logoFile) els.logoFile.focus();
    });
    if (els.form) els.form.addEventListener('submit', e => { if (!allowSubmit) e.preventDefault(); });

    [['center1', 0], ['center2', 1], ['center3', 2]].forEach(([key, idx]) => {
      const el = els[key];
      if (!el) return;
      el.addEventListener('input', () => {
        state.center[idx] = el.value;
        // Typed content toggles the custom-center charge (an empty center is free).
        paintBoard(); updateTotals(); save();
      });
    });

    root.querySelectorAll('[data-mbb-next]').forEach(b => {
      b.addEventListener('click', () => goStep(b.dataset.mbbNext));
    });

    if (els.atc) els.atc.addEventListener('click', addToCart);

    restore();
    // restore() can replace state.options wholesale and can flip any mode —
    // sync the mode UIs and repaint after it, so the highlighted choices match the
    // charges we'd actually add. applyBase() re-pins the managed options.
    applyBase();
    setNamingMode(state.namingMode);
    setCenterMode(state.centerMode);
    setPiecesMode(state.pieces);
    renderOptions();
    paintBoard();
    updateTotals();
    goStep('board');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
